import { APOLLO_LOGO, APOLLO_PRIMARY_LOGO, APOLLO_TAGLINE, APOLLO_COLORS, STRAIN_COLORS } from "@/lib/brand";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadFromUrl } from "@/lib/downloadFromUrl";

// Orange brand mark (crest-only) — used in the Primary Brand Marks section and
// referenced by the incorrect-usage examples below.
const ORANGE_BRAND_MARK = "https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/vhd0tnxq_Apollo_Logo_SpaceMan%20%289%29.webp";

const TYPE_SPECIMENS = [
  { label: "Display", apollo: "Apollo Display", weight: 700, usage: "Headlines, hero statements, large-scale communication." },
  { label: "Heading", apollo: "Apollo Bold", weight: 600, usage: "Subheadings, section titles, and important callouts." },
  { label: "Body", apollo: "Apollo Regular", weight: 400, usage: "Body copy, descriptions, paragraphs, and long-form content." },
  { label: "Labels", apollo: "Apollo Medium", weight: 500, usage: "Labels, navigation, buttons, tags, and small text in all caps." },
];

const VOICE = [
  { title: "Bold", body: "We are fearless in our vision and confident in our voice. We challenge the status quo and push boundaries." },
  { title: "Inspiring", body: "We ignite curiosity and ambition. We communicate the power of discovery and every element of exploration." },
  { title: "Focused", body: "We are clear, concise, and intentional. We cut through the noise to deliver messages that matter." },
  { title: "Human", body: "We are approachable and authentic. We communicate with empathy, respect, and transparency." },
  { title: "Forward Thinking", body: "We speak with optimism and purpose. Our eyes are on the future, and our words help build it." },
];

const DONTS_UNUSED_REMOVED = null;export default function BrandCenter() {
  function copy(hex) {
    navigator.clipboard.writeText(hex);
    toast.success(`${hex} copied`);
  }

  return (
    <div className="space-y-16 slide-up" data-testid="brand-page">
      {/* Hero */}
      <section className="border border-border rounded-sm bg-[#050505] text-white p-10 lg:p-16 relative overflow-hidden">
        <div className="relative z-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-8">Brand Book · V 1.0 · 2026</div>
          <img src={APOLLO_LOGO} alt="Apollo" className="h-16 md:h-24 lg:h-28 w-auto max-w-full object-contain mb-10" style={{ display: "block" }} data-testid="brand-hero-logo" />
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase font-bold tracking-tight leading-[0.95]">
              Engineered<br />for elevation.
            </h1>
            <p className="text-white/70 mt-6 max-w-xl">
              Apollo is a premium cannabis brand engineered for elevation. Inspired by the precision, discipline, and innovation of space exploration, we create products that deliver consistent, superior experiences — every mission, every time.
            </p>
            <div className="mt-10 flex items-center gap-3">
              <span className="w-8 h-px bg-[#FBC90C]" />
              <span className="font-display text-xs uppercase tracking-[0.4em] text-white/80">{APOLLO_TAGLINE}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission strip */}
      <section className="grid md:grid-cols-3 border border-border rounded-sm bg-surface divide-y md:divide-y-0 md:divide-x divide-border">
        {[
          { k: "Mission driven", v: "Every product is engineered against a mission — no coincidences, no shortcuts." },
          { k: "Purpose built", v: "Every SKU exists for a specific ritual. Nothing extra. Everything intentional." },
          { k: "Performance delivered", v: "Consistent, superior experiences — every mission, every time." },
        ].map((c) => (
          <div key={c.k} className="p-8">
            <h3 className="font-display text-2xl uppercase font-bold tracking-tight">{c.k}</h3>
            <p className="text-sm text-muted-foreground mt-2">{c.v}</p>
          </div>
        ))}
      </section>

      {/* Positioning */}
      <section className="grid lg:grid-cols-3 gap-4">
        <Card title="Positioning" body="We don't follow the market. We set the standard. Apollo is calibrated for those who expect precision from the ritual up." />
        <Card title="Vision" body="A world where premium cannabis is treated with the reverence of any aerospace-grade craft — considered, ceremonial, engineered." />
        <Card title="Voice" body="Bold. Inspiring. Focused. Human. Forward thinking. Speak with purpose. Every word should inform, inspire, or connect." />
      </section>

      {/* Palette */}
      <section>
        <SectionHeading eyebrow="Palette" title="Apollo Colors" />
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">Three signal colors. Used with intention. Never in gradients, never blended.</p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {Object.entries(APOLLO_COLORS).map(([key, c]) => (
            <div key={key} className="border border-border rounded-sm overflow-hidden group">
              <div className="h-48 relative" style={{ background: c.hex }}>
                <button onClick={() => copy(c.hex)} className="absolute top-3 right-3 p-2 bg-black/70 text-white rounded-sm opacity-0 group-hover:opacity-100 transition" data-testid={`copy-${key}`} aria-label={`Copy ${c.hex}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="p-4">
                <div className="font-display text-xl uppercase font-bold tracking-tight">{c.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{c.hex}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`pantone-${key}`}>{c.pantone}</div>
                <p className="text-xs text-muted-foreground mt-3">{c.meaning}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Strain application */}
      <section>
        <SectionHeading eyebrow="Application" title="Strain Classification" />
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">The Apollo palette is applied consistently across strain categorization on packaging, merchandising, and digital.</p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {[
            { name: "sativa", label: "SATIVA", color: STRAIN_COLORS.sativa, note: "Uplift, activation — Yellow", ink: "#000", type: "/brand/type-sativa.png" },
            { name: "hybrid", label: "HYBRID", color: STRAIN_COLORS.hybrid, note: "Depth, balance — Orange", ink: "#000", type: "/brand/type-hybrid.png" },
            { name: "indica", label: "INDICA", color: STRAIN_COLORS.indica, note: "Focus, calm — Magenta", ink: "#fff", type: "/brand/type-indica.png" },
          ].map((s) => (
            <div key={s.name} className="border border-border rounded-sm overflow-hidden bg-surface">
              <div className="p-6 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center font-display font-bold uppercase tracking-tight text-lg shrink-0" style={{ background: s.color, color: s.ink }}>
                  {s.label[0]}
                </div>
                <div>
                  <img
                    src={s.type}
                    alt={s.label}
                    className="block w-auto h-[16px] md:h-[19px]"
                    style={{ maxWidth: "none" }}
                    data-testid={`strain-label-${s.name}`}
                  />
                  <div className="text-xs text-muted-foreground mt-2">{s.note}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Voice principles */}
      <section>
        <SectionHeading eyebrow="Voice" title="Five Principles" />
        <div className="grid md:grid-cols-5 gap-4 mt-6">
          {VOICE.map((v, i) => (
            <div key={v.title} className="border border-border rounded-sm p-5 bg-surface">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">0{i + 1}</div>
              <div className="font-display text-xl uppercase font-bold tracking-tight mt-2">{v.title}</div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Typography — Apollo Gothic system */}
      <section>
        <SectionHeading eyebrow="Visual Language" title="Typography" />
        <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Left: Apollo Gothic overview */}
          <div className="border border-border rounded-sm bg-surface p-6 flex flex-col">
            <h3 className="font-display text-4xl uppercase font-bold tracking-tight leading-[0.95]">
              Apollo<br />Gothic
            </h3>
            <span className="w-8 h-px bg-foreground mt-4 mb-6" />
            <div className="space-y-4 text-sm text-muted-foreground leading-relaxed flex-1">
              <p>Our typography is modern, technical, and timeless. It reflects the precision and forward-thinking nature of the Apollo brand.</p>
              <p>We use a clean, geometric sans-serif typeface family for all brand communications.</p>
              <p>Consistency in type usage ensures clarity, hierarchy, and a strong brand presence.</p>
            </div>
          </div>

          {/* Right: 4 weight specimens */}
          <div className="border border-border rounded-sm bg-surface divide-y divide-border">
            {TYPE_SPECIMENS.map((t) => (
              <div key={t.label} className="flex flex-col md:grid md:grid-cols-[130px_100px_1fr_160px] gap-4 md:gap-6 items-start md:items-center p-4 md:p-6">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] mt-1">{t.apollo}</div>
                </div>
                <div className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-none" style={{ fontWeight: t.weight }}>
                  {t.label === "Body" ? "Aa" : "AA"}
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Used For</div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.usage}</p>
                </div>
                <div className="font-mono text-[10px] leading-[1.5] text-muted-foreground break-all">
                  ABCDEFGHIJKLMNOPQ<br />
                  RSTUVWXYZ<br />
                  0123456789<br />
                  !@#$%^&amp;*()
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Primary Wordmark */}
      <section>
        <SectionHeading eyebrow="Logo System" title="Primary Wordmark" />
        <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
          <div className="border border-border rounded-sm bg-surface p-6 flex flex-col">
            <h3 className="font-display text-4xl uppercase font-bold tracking-tight leading-[0.95]">
              Primary<br />Wordmark
            </h3>
            <span className="w-8 h-px bg-foreground mt-4 mb-6" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Apollo primary logo is the core identifier of our brand. It represents precision, exploration, and elevation. The custom wordmark works to communicate a brand that is mission driven and built for what&apos;s next.
            </p>
            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">About the Logo</div>
              <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />The icon represents direction, focus, and the pursuit of higher standards.</li>
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />The star signifies elevation, innovation and the endless pursuit of what&apos;s next.</li>
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />The wordmark is engineered with precision and balance — modern, confident, and unmistakable.</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <div className="border border-border rounded-sm bg-[#050505] p-8 lg:p-20 flex flex-col items-center justify-center gap-6">
              <img src={APOLLO_LOGO} alt="Apollo primary logo" className="h-14 sm:h-20 md:h-28 lg:h-32 w-auto max-w-full object-contain" style={{ display: "block" }} data-testid="primary-logo" />
              <button
                onClick={() => downloadFromUrl(APOLLO_LOGO, "apollo-primary-wordmark")}
                className="border border-white/30 bg-transparent px-4 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] text-white flex items-center gap-2 hover:bg-white hover:text-black transition"
                data-testid="download-primary-wordmark"
              >
                <Download className="h-3 w-3" /> Download Wordmark
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {/* Clear space */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Clear Space</div>
                <div className="bg-[#050505] p-4 rounded-sm relative">
                  <div className="border border-dashed border-white/40 py-4 px-6 relative flex items-center justify-center">
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute top-1/2 -translate-y-1/2 -left-2.5 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute top-1/2 -translate-y-1/2 -right-2.5 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <img src={APOLLO_LOGO} alt="Apollo" className="block" style={{ height: "10px", width: "auto", maxWidth: "none" }} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Clear space around the logo is equal to the height of the &ldquo;x&rdquo;.</p>
              </div>
              {/* Min size */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Minimum Size</div>
                <div className="bg-[#050505] p-6 rounded-sm">
                  <div className="flex items-center justify-center">
                    <img src={APOLLO_LOGO} alt="Apollo" className="block" style={{ height: "8px", width: "auto", maxWidth: "none" }} />
                  </div>
                  <div className="mt-3 border-t border-white/20 pt-1 text-center">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">120px / 0.75in</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Never reproduce the logo smaller than the size shown.</p>
              </div>
              {/* Color usage */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Color Usage</div>
                <div className="bg-[#050505] p-6 rounded-sm flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-white" />
                </div>
                <p className="text-xs text-muted-foreground mt-3">Use in white on dark backgrounds for maximum impact and consistency.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Logo */}
      <section>
        <SectionHeading eyebrow="Logo System" title="Primary Logo" />
        <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
          <div className="border border-border rounded-sm bg-surface p-6 flex flex-col">
            <h3 className="font-display text-4xl uppercase font-bold tracking-tight leading-[0.95]">
              Primary<br />Logo
            </h3>
            <span className="w-8 h-px bg-foreground mt-4 mb-6" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Apollo primary logo pairs the astronaut mission crest with the wordmark. Use this lockup as the hero identifier across brand-defining moments — packaging front-face, retail displays, hero campaigns, and merchandise.
            </p>
            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">About the Logo</div>
              <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />The crest depicts the astronaut planting a flag — a symbol of mission, precision, and elevation.</li>
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />Apollo Orange crest paired with the white wordmark for maximum contrast on dark surfaces.</li>
                <li className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />Keep the crest and wordmark locked together — do not separate, recolor, or re-space.</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <div className="border border-border rounded-sm bg-[#050505] p-6 lg:p-10 flex flex-col items-center justify-center gap-6">
              <img src={APOLLO_PRIMARY_LOGO} alt="Apollo primary lockup logo" className="h-[160px] sm:h-[220px] md:h-[300px] lg:h-[380px] w-auto max-w-full object-contain" style={{ display: "block" }} data-testid="primary-lockup-logo" />
              <button
                onClick={() => downloadFromUrl(APOLLO_PRIMARY_LOGO, "apollo-primary-logo")}
                className="border border-white/30 bg-transparent px-4 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] text-white flex items-center gap-2 hover:bg-white hover:text-black transition"
                data-testid="download-primary-logo"
              >
                <Download className="h-3 w-3" /> Download Primary Logo
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              {/* Clear space */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Clear Space</div>
                <div className="bg-[#050505] p-4 rounded-sm relative">
                  <div className="border border-dashed border-white/40 p-3 relative">
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute top-1/2 -translate-y-1/2 -left-2.5 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <span className="absolute top-1/2 -translate-y-1/2 -right-2.5 bg-[#050505] px-1 font-mono text-[8px] text-white/60">x</span>
                    <img src={APOLLO_PRIMARY_LOGO} alt="Apollo primary lockup" className="h-12 w-auto mx-auto" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Clear space around the lockup is equal to the height of the &ldquo;x&rdquo;.</p>
              </div>
              {/* Min size */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Minimum Size</div>
                <div className="bg-[#050505] p-6 rounded-sm">
                  <img src={APOLLO_PRIMARY_LOGO} alt="Apollo primary lockup" className="h-10 w-auto mx-auto" />
                  <div className="mt-3 border-t border-white/20 pt-1 text-center">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">120px / 0.75in</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Never reproduce the crest smaller than the size shown.</p>
              </div>
              {/* Color usage */}
              <div className="border border-border rounded-sm bg-surface p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Color Usage</div>
                <div className="bg-[#050505] p-6 rounded-sm flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full" style={{ background: "#E15C2C" }} />
                    <div className="w-6 h-6 rounded-full bg-white" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Apollo Orange crest with the white wordmark — always on dark backgrounds.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Brand Marks */}
      <section data-testid="primary-brand-marks">
        <SectionHeading eyebrow="Logo System" title="Primary Brand Marks" />
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          Approved color variants of the Apollo astronaut crest. Use these primary brand marks across packaging, merchandise, and creative applications where color is used to signal category, tier, or campaign.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="brand-mark-yellow">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/vu4aft1l_Astronaut%20Icon%20-%20Yellow%20%288%29.webp"
                alt="Apollo brand mark — yellow"
                className="max-w-full max-h-full w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#FBC90C" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Yellow</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Apollo Yellow crest for sativa-forward products, high-visibility signage, and moments that call for clarity and energy.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl(
                  "https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/vu4aft1l_Astronaut%20Icon%20-%20Yellow%20%288%29.webp",
                  "apollo-brand-mark-yellow"
                )}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-brand-mark-yellow"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>

          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="brand-mark-orange">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/vhd0tnxq_Apollo_Logo_SpaceMan%20%289%29.webp"
                alt="Apollo brand mark — orange"
                className="max-w-full max-h-full w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#E15C2C" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Orange</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Apollo Orange crest — the signature hero mark used for hybrid products, activation moments, and everyday brand touchpoints.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl(
                  "https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/vhd0tnxq_Apollo_Logo_SpaceMan%20%289%29.webp",
                  "apollo-brand-mark-orange"
                )}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-brand-mark-orange"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>

          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="brand-mark-magenta">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/j03ehjv2_Astronaut%20Icon%20-%20Purple%20%288%29.webp"
                alt="Apollo brand mark — magenta"
                className="max-w-full max-h-full w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#B02065" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Magenta</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Apollo Magenta crest — a controlled accent reserved for indica products, premium tier drops, and moments of distinction.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl(
                  "https://customer-assets-7cd3h4nn.emergentagent.net/job_brand-command-22/artifacts/j03ehjv2_Astronaut%20Icon%20-%20Purple%20%288%29.webp",
                  "apollo-brand-mark-magenta"
                )}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-brand-mark-magenta"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary Brand Marks */}
      <section data-testid="secondary-brand-marks">
        <SectionHeading eyebrow="Logo System" title="Secondary Brand Marks" />
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          Simplified brand marks for constrained placements — app icons, favicons, social avatars, packaging seals, and any square-format touchpoint where the full crest cannot be reproduced with fidelity.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="secondary-mark-yellow">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="/brand/secondary-sativa-yellow.png"
                alt="Apollo secondary brand mark — yellow"
                className="max-w-[58%] max-h-[58%] w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#FBC90C" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Yellow</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Sativa-tier applications — high-visibility icon for app tiles, social avatars, and category signage.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl("/brand/secondary-sativa-yellow.png", "apollo-secondary-brand-mark-yellow")}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-secondary-mark-yellow"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>

          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="secondary-mark-orange">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="/brand/secondary-hybrid-orange.png"
                alt="Apollo secondary brand mark — orange"
                className="max-w-[58%] max-h-[58%] w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#E15C2C" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Orange</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Hybrid-tier applications — the signature everyday icon for app stores, retail seals, and packaging accents.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl("/brand/secondary-hybrid-orange.png", "apollo-secondary-brand-mark-orange")}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-secondary-mark-orange"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>

          <div className="border border-border rounded-sm overflow-hidden bg-surface flex flex-col" data-testid="secondary-mark-magenta">
            <div className="aspect-square flex items-center justify-center p-8 bg-black overflow-hidden">
              <img
                src="/brand/secondary-indica-magenta.png"
                alt="Apollo secondary brand mark — magenta"
                className="max-w-[58%] max-h-[58%] w-auto h-auto object-contain block"
              />
            </div>
            <div className="p-5 border-t border-border flex flex-col gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: "#B02065" }} />
                  <div className="font-display text-xl uppercase font-bold tracking-tight">Brand Mark — Magenta</div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Indica-tier applications — reserved icon for premium drops, limited seals, and moments of distinction.
                </p>
              </div>
              <button
                onClick={() => downloadFromUrl("/brand/secondary-indica-magenta.png", "apollo-secondary-brand-mark-magenta")}
                className="mt-1 border border-border bg-transparent px-3 py-2 uppercase text-[10px] font-semibold tracking-[0.25em] flex items-center justify-center gap-2 hover:bg-surface-elev transition"
                data-testid="download-secondary-mark-magenta"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Incorrect Usage grid */}
      <section>
        <SectionHeading eyebrow="Logo Usage" title="Incorrect Usage" />
        <div className="mt-6 grid lg:grid-cols-[280px_1fr] gap-6">
          <div className="border border-border rounded-sm bg-surface p-6 flex flex-col">
            <div className="space-y-4 text-sm text-muted-foreground leading-relaxed flex-1">
              <p>To protect the integrity of the Apollo brand, the logo must be used correctly and consistently.</p>
              <p>Never alter, distort, recolor, or add effects to the logo. Avoid using it on busy or low-contrast backgrounds that reduce legibility.</p>
              <p>Always use the approved logo files and follow the guidelines in this document.</p>
            </div>
            <div className="mt-6 border rounded-sm p-4 flex gap-3" style={{ borderColor: "rgba(225,92,44,0.4)" }}>
              <div className="w-8 h-8 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: "#E15C2C" }}>
                <span className="text-lg leading-none" style={{ color: "#E15C2C" }}>!</span>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-1">Consistency Builds Trust</div>
                <p className="text-xs text-muted-foreground">Follow these rules to keep the Apollo brand strong and recognizable.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="incorrect-usage-grid">
            <IncorrectTile label="Do not stretch or distort">
              <img src={APOLLO_LOGO} alt="Apollo distorted" className="w-1/2 h-auto" style={{ transform: "scaleY(2.4) scaleX(0.7)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not rotate the logo">
              <img src={APOLLO_LOGO} alt="Apollo rotated" className="w-40 h-auto" style={{ transform: "rotate(-14deg)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not add taglines">
              <div className="flex flex-col items-center gap-1">
                <img src={APOLLO_LOGO} alt="Apollo" className="w-32 h-auto" />
                <span className="font-mono text-[8px] uppercase tracking-[0.35em] text-white">Elevate Beyond.</span>
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not place on busy backgrounds" busy>
              <img src={APOLLO_LOGO} alt="Apollo busy" className="w-40 h-auto" />
            </IncorrectTile>

            <IncorrectTile label="Do not use on low contrast" light>
              <img src={APOLLO_LOGO} alt="Apollo low contrast" className="w-40 h-auto" />
            </IncorrectTile>

            <IncorrectTile label="Do not alter the typography">
              <span className="text-3xl tracking-[0.15em] text-white" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400 }}>
                A P O L L O
              </span>
            </IncorrectTile>

            {/* Primary Logo + Brand Mark misuses */}
            <IncorrectTile label="Do not separate crest and wordmark">
              <div className="flex items-center gap-14">
                <img src={ORANGE_BRAND_MARK} alt="Apollo crest" className="h-14 w-auto" />
                <img src={APOLLO_LOGO} alt="Apollo wordmark" className="h-3 w-auto" />
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not recolor the brand mark">
              <img src={ORANGE_BRAND_MARK} alt="Apollo crest recolored" className="h-24 w-auto" style={{ filter: "hue-rotate(140deg) saturate(1.4)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not flip the primary logo">
              <img src={APOLLO_PRIMARY_LOGO} alt="Apollo logo flipped" className="h-24 w-auto" style={{ transform: "scaleX(-1)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not rotate the brand mark">
              <img src={ORANGE_BRAND_MARK} alt="Apollo crest rotated" className="h-24 w-auto" style={{ transform: "rotate(28deg)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not add drop shadows">
              <img src={APOLLO_PRIMARY_LOGO} alt="Apollo logo shadow" className="h-24 w-auto" style={{ filter: "drop-shadow(4px 6px 0 #FBC90C) drop-shadow(-4px -4px 0 #B02065)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not enclose the brand mark">
              <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: "#FBC90C" }}>
                <img src={ORANGE_BRAND_MARK} alt="Apollo crest badge" className="h-16 w-auto" />
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not fade or lower opacity">
              <img src={APOLLO_PRIMARY_LOGO} alt="Apollo logo faded" className="h-24 w-auto" style={{ opacity: 0.22 }} />
            </IncorrectTile>

            <IncorrectTile label="Do not use on same-color background" bg="#E15C2C">
              <img src={ORANGE_BRAND_MARK} alt="Apollo crest low contrast" className="h-24 w-auto" />
            </IncorrectTile>

            <IncorrectTile label="Do not rescale crest and wordmark independently">
              <div className="flex flex-col items-center gap-1">
                <img src={ORANGE_BRAND_MARK} alt="Apollo crest oversized" className="h-24 w-auto" />
                <img src={APOLLO_LOGO} alt="Apollo wordmark undersized" className="h-1.5 w-auto" />
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not overlay text on the primary logo">
              <div className="relative">
                <img src={APOLLO_PRIMARY_LOGO} alt="Apollo logo overlay" className="h-24 w-auto" />
                <span className="absolute inset-0 flex items-center justify-center font-display text-2xl uppercase font-bold tracking-tight text-white" style={{ textShadow: "2px 2px 0 #000" }}>
                  Sale
                </span>
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not fill the brand mark with a gradient">
              <div className="relative h-24 w-24">
                <img src={ORANGE_BRAND_MARK} alt="Apollo crest gradient" className="h-24 w-24 object-contain" style={{ filter: "hue-rotate(-60deg)" }} />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 0%, #B02065 100%)", mixBlendMode: "multiply" }} />
              </div>
            </IncorrectTile>

            <IncorrectTile label="Do not frame the primary logo">
              <div className="p-3 border-4" style={{ borderColor: "#FBC90C", background: "#050505" }}>
                <img src={APOLLO_PRIMARY_LOGO} alt="Apollo logo framed" className="h-16 w-auto" />
              </div>
            </IncorrectTile>

            {/* Secondary Brand Mark misuses */}
            <IncorrectTile label="Do not distort the secondary mark">
              <img src="/brand/secondary-sativa-yellow.png" alt="Apollo secondary yellow distorted" className="h-24 w-auto" style={{ transform: "scaleX(1.9) scaleY(0.65)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not add effects to the secondary mark">
              <img src="/brand/secondary-indica-magenta.png" alt="Apollo secondary magenta with glow" className="h-24 w-auto" style={{ filter: "drop-shadow(0 0 14px #22d3ee) drop-shadow(0 0 8px #FBC90C)" }} />
            </IncorrectTile>

            <IncorrectTile label="Do not recolor the secondary mark">
              <img src="/brand/secondary-sativa-yellow.png" alt="Apollo secondary mark recolored" className="h-24 w-auto" style={{ filter: "hue-rotate(160deg) saturate(1.4)" }} />
            </IncorrectTile>
          </div>
        </div>
      </section>
    </div>
  );
}

function IncorrectTile({ label, children, busy = false, light = false, bg = null }) {
  const background = bg
    ? bg
    : busy
      ? "linear-gradient(135deg, #E15C2C 0%, #FBC90C 40%, #B02065 100%)"
      : light
        ? "#EDEDED"
        : "#050505";
  return (
    <div className="space-y-2" data-testid={`incorrect-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div
        className="aspect-[5/3] rounded-sm border border-border flex items-center justify-center overflow-hidden"
        style={{ background }}
      >
        {children}
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0" style={{ background: "#E15C2C" }}>
          <svg viewBox="0 0 10 10" className="w-2 h-2 text-white"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

function Card({ title, body }) {
  return (
    <div className="border border-border rounded-sm p-6 bg-surface hover-lift">
      <h3 className="font-display text-2xl uppercase font-bold tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1">{eyebrow}</div>
      <h2 className="font-display text-2xl md:text-3xl uppercase font-semibold tracking-tight">{title}</h2>
    </div>
  );
}
